import { Tooltip } from "antd";
import styled from "styled-components";
import { AppTag } from "@lib/components/AppComponents";
import { randomColor } from "@lib/helpers/ColorHelper";

type Props = {
  keywords: string[] | null | undefined;
  wrap?: boolean; // If true, shows all keywords with wrap, otherwise shows truncated with ellipsis
};

const Wrapper = styled.div`
  width: 100%;
  min-width: 0;
  max-width: 100%;
`;

const KeywordsContainer = styled.div<{ $wrap?: boolean }>`
  display: flex;
  flex-wrap: ${({ $wrap }) => ($wrap ? "wrap" : "nowrap")};
  overflow: ${({ $wrap }) => ($wrap ? "visible" : "hidden")};
  gap: 4px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

const KeywordTag = styled(AppTag)`
  flex-shrink: 0;
  white-space: nowrap;
`;

const EllipsisTag = styled(AppTag)`
  flex-shrink: 0;
  white-space: nowrap;
`;

// Simple hash function to get consistent color for each keyword
const getKeywordColor = (keyword: string, index: number): string => {
  let hash = 0;
  for (let i = 0; i < keyword.length; i++) {
    hash = keyword.charCodeAt(i) + ((hash << 5) - hash);
  }
  return randomColor(index);
};

const Keywords = (props: Props) => {
  const { keywords, wrap = false } = props;

  if (!keywords || keywords.length === 0) {
    return <span>-</span>;
  }

  // If wrap is enabled, show all keywords with wrap
  if (wrap) {
    return (
      <Wrapper>
        <KeywordsContainer $wrap={wrap}>
          {keywords.map((keyword, index) => (
            <KeywordTag key={index} color={getKeywordColor(keyword, index)}>
              {keyword}
            </KeywordTag>
          ))}
        </KeywordsContainer>
      </Wrapper>
    );
  }

  // If wrap is disabled, show only first keyword + ellipsis, tooltip shows all
  const hasMoreThanOne = keywords.length > 1;
  const firstKeyword = keywords[0];

  const content = (
    <Wrapper>
      <KeywordsContainer $wrap={false}>
        <KeywordTag color={getKeywordColor(firstKeyword, 0)}>
          {firstKeyword}
        </KeywordTag>
        {hasMoreThanOne && (
          <EllipsisTag color="#d9d9d9" style={{ color: "#666" }}>
            ...
          </EllipsisTag>
        )}
      </KeywordsContainer>
    </Wrapper>
  );

  // Show tooltip with all keywords if there are more than one
  if (hasMoreThanOne) {
    return (
      <Tooltip
        title={
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {keywords.map((keyword, index) => (
              <AppTag key={index} color={getKeywordColor(keyword, index)}>
                {keyword}
              </AppTag>
            ))}
          </div>
        }
      >
        {content}
      </Tooltip>
    );
  }

  return content;
};

export default Keywords;
